import { useState, useEffect, useRef, useMemo } from 'react'
import JSZip from 'jszip'
import { apiService } from '../../services/api'
import { UserProfile } from '../../types'

interface HeadshotImage {
  key: string
  thumbnailKey: string
  fileName: string
  uploadedAt: string
  linkedUserId?: string
  thumbnailUrl?: string
}

interface UploadResult {
  processed: number
  skipped: string[]
  total: number
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png']

function isImageFile(filename: string): boolean {
  const lower = filename.toLowerCase()
  return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext))
}

export default function HeadshotAdmin() {
  // Upload state
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Linking state
  const [headshots, setHeadshots] = useState<HeadshotImage[]>([])
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loadingHeadshots, setLoadingHeadshots] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [selectedHeadshot, setSelectedHeadshot] = useState<HeadshotImage | null>(null)
  const [pilotSearch, setPilotSearch] = useState('')
  const [selectedPilot, setSelectedPilot] = useState<UserProfile | null>(null)
  const [showPilotDropdown, setShowPilotDropdown] = useState(false)
  const [linkConfirm, setLinkConfirm] = useState(false)
  const [linking, setLinking] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadHeadshots()
    loadUsers()
  }, [])

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(t)
    }
  }, [toast])

  const loadHeadshots = async () => {
    setLoadingHeadshots(true)
    const response = await apiService.listHeadshots()
    if (response.data) {
      const data = response.data as any
      const list = data?.headshots || []
      setHeadshots(Array.isArray(list) ? list : [])
    } else {
      setHeadshots([])
    }
    setLoadingHeadshots(false)
  }

  const loadUsers = async () => {
    setLoadingUsers(true)
    const response = await apiService.getAllUsers()
    if (response.data) {
      const data = response.data as any
      const list = data?.users || []
      setUsers(Array.isArray(list) ? list : [])
    } else {
      setUsers([])
    }
    setLoadingUsers(false)
  }

  const unlinkedHeadshots = useMemo(
    () => headshots.filter(h => !h.linkedUserId),
    [headshots]
  )

  const filteredPilots = useMemo(() => {
    if (!pilotSearch.trim()) return users
    const q = pilotSearch.toLowerCase()
    return users.filter(
      u =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    )
  }, [users, pilotSearch])

  // --- ZIP Upload ---
  const handleZipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setUploadError(null)
    setUploadResult(null)
    setUploadProgress({ current: 0, total: 0 })

    try {
      const zip = await JSZip.loadAsync(file)
      const entries = Object.keys(zip.files).filter(name => {
        const entry = zip.files[name]
        return !entry.dir && !name.startsWith('__MACOSX')
      })

      const imageFiles: string[] = []
      const skipped: string[] = []

      for (const name of entries) {
        if (isImageFile(name)) {
          imageFiles.push(name)
        } else {
          skipped.push(name.split('/').pop() || name)
        }
      }

      if (imageFiles.length === 0) {
        setUploadError('No valid images found in ZIP file. Only JPEG and PNG files are supported.')
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      setUploadProgress({ current: 0, total: imageFiles.length })
      let processed = 0

      for (const name of imageFiles) {
        const basename = name.split('/').pop() || name
        try {
          // Get presigned URL
          const urlResponse = await apiService.uploadHeadshotUrl(basename)
          if (urlResponse.error) {
            skipped.push(basename)
            continue
          }

          const { uploadUrl } = urlResponse.data as any

          // Extract file data from ZIP
          const blob = await zip.files[name].async('blob')

          // Determine content type
          const ext = basename.toLowerCase()
          const contentType = ext.endsWith('.png') ? 'image/png' : 'image/jpeg'

          // Upload to S3 via presigned URL
          const putResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': contentType },
          })

          if (putResponse.ok) {
            processed++
          } else {
            skipped.push(basename)
          }
        } catch {
          skipped.push(basename)
        }

        setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }))
      }

      setUploadResult({ processed, skipped, total: entries.length })
      // Refresh headshots list after upload
      loadHeadshots()
    } catch {
      setUploadError('Failed to process ZIP file. Please ensure the file is a valid ZIP archive.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // --- Linking ---
  const handleSelectHeadshot = (headshot: HeadshotImage) => {
    setSelectedHeadshot(headshot)
    setSelectedPilot(null)
    setPilotSearch('')
    setLinkConfirm(false)
  }

  const handleSelectPilot = (pilot: UserProfile) => {
    setSelectedPilot(pilot)
    setPilotSearch(`${pilot.firstName} ${pilot.lastName}`)
    setShowPilotDropdown(false)
    setLinkConfirm(false)
  }

  const handleLinkConfirm = async () => {
    if (!selectedHeadshot || !selectedPilot) return
    setLinking(true)
    const response = await apiService.linkHeadshot(selectedPilot.userId, selectedHeadshot.key)
    if (response.error) {
      setToast({ message: response.error.message || 'Failed to link headshot', type: 'error' })
    } else {
      setToast({ message: `Headshot linked to ${selectedPilot.firstName} ${selectedPilot.lastName}`, type: 'success' })
      setSelectedHeadshot(null)
      setSelectedPilot(null)
      setPilotSearch('')
      setLinkConfirm(false)
      loadHeadshots()
    }
    setLinking(false)
  }

  const progressPercent =
    uploadProgress.total > 0
      ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
      : 0

  return (
    <div className="space-y-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      {/* Section 1: ZIP Upload */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Bulk Headshot Upload</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a ZIP file containing pilot headshot photos. Only JPEG and PNG files will be processed.
        </p>

        <div className="flex items-center gap-4">
          <label
            htmlFor="zip-upload"
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors cursor-pointer ${
              uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? 'Uploading...' : 'Select ZIP File'}
            <input
              id="zip-upload"
              ref={fileInputRef}
              type="file"
              accept=".zip"
              disabled={uploading}
              onChange={handleZipUpload}
              className="hidden"
            />
          </label>
        </div>

        {/* Progress */}
        {uploading && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Uploading images...</span>
              <span>{uploadProgress.current} / {uploadProgress.total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Upload Error */}
        {uploadError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{uploadError}</p>
          </div>
        )}

        {/* Upload Summary */}
        {uploadResult && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">Upload Complete</h4>
            <p className="text-sm text-green-800">
              {uploadResult.processed} image{uploadResult.processed !== 1 ? 's' : ''} uploaded successfully
              out of {uploadResult.total} file{uploadResult.total !== 1 ? 's' : ''} in the archive.
            </p>
            {uploadResult.skipped.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-amber-800">
                  Skipped {uploadResult.skipped.length} file{uploadResult.skipped.length !== 1 ? 's' : ''}:
                </p>
                <ul className="mt-1 text-sm text-amber-700 list-disc list-inside max-h-32 overflow-y-auto">
                  {uploadResult.skipped.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 2: Headshot Linking */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Link Headshots to Pilots</h3>

        {loadingHeadshots ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : unlinkedHeadshots.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No unlinked headshots available. Upload a ZIP file above to add headshots.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Select a headshot, then search for a pilot to link it to. {unlinkedHeadshots.length} unlinked headshot{unlinkedHeadshots.length !== 1 ? 's' : ''}.
            </p>

            {/* Thumbnail Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 mb-6">
              {unlinkedHeadshots.map(headshot => (
                <div key={headshot.key} className="flex flex-col items-center">
                  <button
                    onClick={() => handleSelectHeadshot(headshot)}
                    className={`relative aspect-square w-full rounded-lg overflow-hidden border-2 transition-all ${
                      selectedHeadshot?.key === headshot.key
                        ? 'border-blue-600 ring-2 ring-blue-300'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {headshot.thumbnailUrl ? (
                      <img
                        src={headshot.thumbnailUrl}
                        alt={headshot.fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    {selectedHeadshot?.key === headshot.key && (
                      <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                  <p className="text-[10px] text-gray-500 mt-1 truncate w-full text-center" title={headshot.fileName}>
                    {headshot.fileName}
                  </p>
                </div>
              ))}
            </div>

            {/* Pilot Search & Link */}
            {selectedHeadshot && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-start gap-4">
                  {/* Selected headshot preview */}
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      {selectedHeadshot.thumbnailUrl ? (
                        <img src={selectedHeadshot.thumbnailUrl} alt={selectedHeadshot.fileName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate max-w-[80px]">{selectedHeadshot.fileName}</p>
                  </div>

                  {/* Pilot search */}
                  <div className="flex-1 relative">
                    <label htmlFor="pilot-search" className="block text-sm font-medium text-gray-700 mb-1">
                      Search Pilot
                    </label>
                    {loadingUsers ? (
                      <p className="text-sm text-gray-500">Loading pilots...</p>
                    ) : (
                      <>
                        <input
                          id="pilot-search"
                          type="text"
                          value={pilotSearch}
                          onChange={e => {
                            setPilotSearch(e.target.value)
                            setSelectedPilot(null)
                            setShowPilotDropdown(true)
                            setLinkConfirm(false)
                          }}
                          onFocus={() => setShowPilotDropdown(true)}
                          placeholder="Type a name or email..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        {showPilotDropdown && !selectedPilot && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredPilots.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-500">No pilots found</div>
                            ) : (
                              filteredPilots.slice(0, 20).map(pilot => (
                                <button
                                  key={pilot.userId}
                                  onClick={() => handleSelectPilot(pilot)}
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex justify-between items-center"
                                >
                                  <span className="font-medium text-gray-900">{pilot.firstName} {pilot.lastName}</span>
                                  <span className="text-gray-500 text-xs">{pilot.email}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Selected pilot display */}
                    {selectedPilot && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {selectedPilot.firstName} {selectedPilot.lastName}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedPilot(null)
                            setPilotSearch('')
                            setLinkConfirm(false)
                          }}
                          className="text-gray-400 hover:text-gray-600 text-sm"
                        >
                          &times;
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Link action */}
                {selectedPilot && (
                  <div className="mt-4 flex items-center gap-3">
                    {!linkConfirm ? (
                      <button
                        onClick={() => setLinkConfirm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Link Headshot
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-sm text-amber-800">
                          Link <span className="font-medium">{selectedHeadshot.fileName}</span> to{' '}
                          <span className="font-medium">{selectedPilot.firstName} {selectedPilot.lastName}</span>?
                        </p>
                        <button
                          onClick={handleLinkConfirm}
                          disabled={linking}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          {linking ? 'Linking...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setLinkConfirm(false)}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export { isImageFile }
